import React, {
  useMemo,
  Fragment,
  useEffect,
} from "react";
import {
  MaterialReactTable,
  MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
} from "material-react-table";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useOutletContext } from "react-router-dom";
import { dateformatConverter } from "../../../../common/utilities/dates/dateformatConverter";
import { useViewReferralsListMutation } from "../../../../reactQueries/AppApi/rqHooksForReferrals";
import { assignReferralEditPath } from "../../../../routes/routePaths";
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import NoTableDataComp from "../../../../common/otherCommonComponents/NoTableDataComp";
import HtmlTooltip from "../../../../common/tooltips/HtmlTooltip";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useAccessTypes } from "../../../../hooksAndFunctions/customHooks/useAccessTypes";
import ExportButton from "../../../../common/otherCommonComponents/ExportButton";
import { useTrackActivityMutation } from "../../../../reactQueries/commonRQHooks";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || '';
};

const ViewAssignReferralReq = () => {
  const { mutate: trackActivityMutate } = useTrackActivityMutation();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const { clientPortalAdminType: { hasA1Access } } = useAccessTypes();
  const { clientPortalAdminRoleId, isEnterpriseAdmin, id: userId } = loggedInUser;

  const canExportXls = isEnterpriseAdmin || hasA1Access;

  const { mutate: getReferralsList, isSuccess, data: referralsData, isError, isLoading: isLoadingOnReferralData } = useViewReferralsListMutation();


  useEffect(() => {
    getReferralsList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
  }, [loggedInUser])

  const referralsCMSData = useOutletContext();
  
  const fetchAssignRefReqListLabel = (key) => {
    const labels = referralsCMSData?.pageContent?.AssignReferralRequestsList?.Labels;
    return returnLabel(key, labels);
  };

  const fetchAssignRefReqTitle = () => {
    return referralsCMSData?.pageContent?.AssignReferralRequest?.Title || "";
  };

  const fetchCommonCMSLabel = (key) => {
    const labels = referralsCMSData?.pageContent?.CommonComponent?.Labels;
    return returnLabel(key, labels);
  };

  const referralRequestTitle =
    fetchAssignRefReqTitle() || referralsCMSData?.title || "Assign Referral Requests";
  const exportFileName = `${referralRequestTitle}.xlsx`;
  const exportWorksheetName = referralRequestTitle;

  const exportColumns = [
    { id: 'documentName', header: fetchAssignRefReqListLabel("AssignReferralRequestsList.ReferralName"), getValue: (row) => row?.DocumentName || '' },
    { id: 'DocumentStatus', header: fetchAssignRefReqListLabel("AssignReferralRequestsList.Status"), getValue: (row) => row?.DocumentStatus || '' },
    { id: 'clientAccountName', header: fetchAssignRefReqListLabel("AssignReferralRequestsList.ClientAccount"), getValue: (row) => row?.ClientAccountName || '' },
    { id: 'CreatedBy', header: fetchAssignRefReqListLabel("AssignReferralRequestsList.SubmittedBy"), getValue: (row) => row?.CreatedBy || '' },
    { id: 'submittedOn', header: fetchAssignRefReqListLabel("AssignReferralRequestsList.SubmittedOn"), getValue: (row) => row?.SubmittedOn ? dateformatConverter(row.SubmittedOn, "DD MMM YYYY hh:mm A") : '' },
    { id: 'assignedTo', header: fetchAssignRefReqListLabel("AssignReferralRequestsList.AssignedTo"), getValue: (row) => row?.AssignedTo || '' },
    { id: 'assignedOn', header: fetchAssignRefReqListLabel("AssignReferralRequestsList.AssignedOn"), getValue: (row) => row?.AssignedOn ? dateformatConverter(row.AssignedOn, "DD MMM YYYY hh:mm A") : '' },
  ];
  
  const columns = useMemo(
    () => [
      {
        id: "documentName",
        header: fetchAssignRefReqListLabel ("AssignReferralRequestsList.ReferralName"), //"Referral Name",
        accessorFn: (originalRow) => {
          const docName = originalRow.DocumentName;

          return docName
        },
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchAssignRefReqListLabel ("AssignReferralRequestsList.PlaceholderReferralName") }
      },
      {
        accessorKey: "DocumentStatus",
        header: fetchAssignRefReqListLabel ("AssignReferralRequestsList.Status"), //"Status",
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchAssignRefReqListLabel ("AssignReferralRequestsList.PlaceholderStatus") }
      },
      {
        accessorFn: (row) => {
          const val = row.ClientAccountName
          return val || ""
        },
        id: 'clientAccountName',
        header: fetchAssignRefReqListLabel ("AssignReferralRequestsList.ClientAccount"), //"Client Account",
        Cell: TooltipCell,
        filterFn: 'contains',        
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchAssignRefReqListLabel ("AssignReferralRequestsList.PlaceholderClientAccount") }
      },
      {
        accessorKey: "CreatedBy",
        header: fetchAssignRefReqListLabel ("AssignReferralRequestsList.SubmittedBy"), //"Submitted By",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchAssignRefReqListLabel ("AssignReferralRequestsList.PlaceholderSubmittedBy") }
      },
      {
        accessorFn: (originalRow) => {
          const data = dateformatConverter(
            originalRow.SubmittedOn,
            "DD MMM YYYY hh:mm A"
          );
          return `${data}`;
        },
        id: "submittedOn",
        header: fetchAssignRefReqListLabel ("AssignReferralRequestsList.SubmittedOn"), //"Submitted On",
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchAssignRefReqListLabel ("AssignReferralRequestsList.PlaceholderSubmittedOn") }
      },
      {
        accessorFn: (row) => {
          const val = row.AssignedTo
          return val || ""
        },
        id: 'assignedTo',
        header: fetchAssignRefReqListLabel ("AssignReferralRequestsList.AssignedTo"), //"Assigned To",
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchAssignRefReqListLabel ("AssignReferralRequestsList.PlaceholderAssignedTo") }
      },
      {
        accessorFn: (originalRow) => {
          const data = dateformatConverter(
            originalRow.AssignedOn,
            "DD MMM YYYY hh:mm A"
          );
          return `${data}` || "";
        },
        id: "assignedOn",
        header: fetchAssignRefReqListLabel ("AssignReferralRequestsList.AssignedOn"), //"Assigned On",
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchAssignRefReqListLabel ("AssignReferralRequestsList.PlaceholderAssignedOn") }
      },
    ],
    [referralsCMSData]
  );

  /**redirect hook */
  const navigate = useNavigate();

  return (
    <Fragment>
      {
        !!isLoadingOnReferralData && fetchCommonCMSLabel('Common.Waitloading') //'Wait loading...'
      }

      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {/* Referral requests to assign */}
        {fetchAssignRefReqListLabel("AssignReferralRequestsList.ReferralRequestTitle")}
      </Typography>

      {/**incase successful call but empty response */}
      {!!isSuccess && !referralsData?.data?.length && (<NoTableDataComp />)}

      {
        !!isSuccess && !!referralsData?.data?.length && (
          <MaterialReactTable
            columns={columns}
            data={referralsData?.data || []}
            layoutMode="grid"
            enableGlobalFilter={false}
            enableDensityToggle={false}

            initialState={{
              density: "compact",
              showColumnFilters: true,
            }}
            state={{
              isLoading: isLoadingOnReferralData,
              columnPinning: {
                left: ["mrt-row-actions", "documentName"],
              },
            }}
            enableStickyHeader

            displayColumnDefOptions={{
              'mrt-row-actions': {
                header: fetchAssignRefReqListLabel('AssignReferralRequestsList.Actions'), //Actions
              },
            }}
            muiTablePaginationProps={{
              labelRowsPerPage: fetchCommonCMSLabel('Common.Rowsperpage') //Rows per page
            }}
               renderToolbarInternalActions={({ table }) => (
                                    <>
                                      {canExportXls && (
                                        <ExportButton
                                          table={table}
                                          tooltipTitle={fetchAssignRefReqListLabel("AssignReferralRequestsList.ExportToXLS")}
                                          trackActivity={trackActivityMutate}
                                          userId={userId}
                                          exportColumns={exportColumns}
                                          fileName={exportFileName}
                                          worksheetName={exportWorksheetName}
                                        />
                                      )}
                                      <MRTToggleFiltersButton table={table} />
                                      <MRTShowHideColumnsButton table={table} />
                                      <MRTFullScreenToggleButton table={table} />
                                    </>
                                  )}
            enableRowActions
            renderRowActions={({ row }) => {
              const Id = row.original.Id;
              return (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                  <IconButton
                    onClick={() => {
                      navigate(assignReferralEditPath({ id: Id }));
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              )

            }}
          />
        )
      }
      <MpgErrorDialog
        open={!!isError}
      />
    </Fragment>
  );
};
export default ViewAssignReferralReq;
